import React, { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Trash2, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function TourPhotos({ tour }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = tour.photos || [];

  const del = useMutation({
    mutationFn: async (photoId) => (await api.delete(`/buyer/tours/${tour.id}/photos/${photoId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tours"] }),
  });
  const saveCaption = useMutation({
    mutationFn: async ({ photoId, caption }) => (await api.put(`/buyer/tours/${tour.id}/photos/${photoId}/caption`, { caption })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tours"] }); toast.success("Note saved"); },
  });

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/buyer/tours/${tour.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["tours"] });
      toast.success("Photo added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-4" data-testid={`tour-photos-${tour.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Camera className="h-3.5 w-3.5" /> Your tour photos ({photos.length})
        </p>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`photo-input-${tour.id}`} />
        <Button size="sm" variant="outline" className="rounded-full h-8 gap-1" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`photo-add-${tour.id}`}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Add photo
        </Button>
      </div>
      {photos.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} className="w-32" data-testid={`photo-thumb-${p.id}`}>
              <div className="relative group h-24 w-32 rounded-xl overflow-hidden bg-secondary">
                <img src={`${BACKEND}/api/tours/${tour.id}/photos/${p.id}`} alt={p.filename} className="h-full w-full object-cover" />
                <button onClick={() => del.mutate(p.id)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-delete-${p.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
              <Input
                defaultValue={p.caption || ""}
                placeholder="Add a note…"
                className="mt-1 h-8 text-xs rounded-lg"
                onBlur={(e) => { if (e.target.value !== (p.caption || "")) saveCaption.mutate({ photoId: p.id, caption: e.target.value }); }}
                data-testid={`photo-caption-${p.id}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-1.5">Snap photos during the visit so homes don't blur together later.</p>
      )}
    </div>
  );
}
